import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { UnfoldMoreSharp, UnfoldLessSharp } from '@mui/icons-material';
import GameCard from './GameCard';
import LockedGameCard from './GameCard/LockedGameCard';
import BetSlip from './BetSlip';
import { useGameContext } from '../context/GameContext';
import { useCurrentWeek } from '../context/CurrentWeekContext';
import { useSeason } from '../context/SeasonContext';
import { useMobile } from '../context/MobileContext';
import styles from '../styles/WeeklyPicks.module.css';
import { getSocket, initSocket } from '../api';
import { useAuth } from '../context/AuthContext';

import { useNavigate } from 'react-router-dom';
import MobileWeeklyPickSheet from './mobile/MobileWeeklyPickSheet';
import { useFilterContext } from '../context/FilterContext';
import { applyFilters } from '../utils/filterUtils';
import { useSortContext } from '../context/SortContext';
import { sortAndGroupGames } from '../utils/sortUtils';
import IntegratedHeader from './IntegratedHeader';
import { useLeagueSettings } from '../context/LeagueSettingsContext';
import { formatWeekLabel } from '../utils/weekLabel';
import GameCardSkeletonGrid from './loading/GameCardSkeletonGrid';
import GameDetailModal from './GameDetailModal/GameDetailModal';

// Utility functions outside component
const getGameStatus = (game) => {
  if (game.game_status === 'completed') return 'completed';
  if (game.game_status === 'in_progress') return 'inProgress';
  return 'scheduled';
};

const sortGamesByDate = (games) => {
  return [...games].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
};

// Note: createLockedGamesMap function removed - replaced with enhanced game processing logic

const WeeklyPickSheet = () => {
  // ALL hooks must be called first, before any conditional logic
  const { user } = useAuth();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const locksRef = useRef(null);

  const {
    games,
    userPicks,
    animatingGames,
    fetchGames,
    handlePickUpdate,
    fetchLiveGameData,
    watchedPicks,
    fetchWatchedPicks,
    setWatchedPicks,
    isPeeking,
    fetchLocksForWeek,
    locksCache,
    byeWeekState,
    fetchUserSeasonLocks,
    getLocksForWeek,
    getMaxPicksForWeek,
    loadedGamesWeek,
    isGamesLoading,
    toggleWatchPick,
    requestDirectLock,
  } = useGameContext();

  const { currentWeek, calendar, isLoading: isWeekAndCalendarLoading, fetchCurrentWeekAndCalendar } = useCurrentWeek();
  const { effectiveSeason } = useSeason();
  const { filters } = useFilterContext();
  const { sortSettings, toggleGroupCollapse, isGroupCollapsed } = useSortContext();
  const { canSeeLocks, canSeeLockers } = useLeagueSettings();
  const [displayedWeek, setDisplayedWeek] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isBetSlipOpen, setIsBetSlipOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [gameDetailState, setGameDetailState] = useState(null);

  // ALL useEffect hooks must be called before any conditional returns
  useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Step 1: Initialize socket and fetch current week and calendar
  useEffect(() => {
    const initialize = async () => {
      console.log('1. Initializing socket and fetching current week and calendar');
      if (user) {
        initSocket();
        await fetchCurrentWeekAndCalendar();
        setIsInitialized(true);
        setIsLoading(false);
      }
    };
    initialize();
  }, [user, fetchCurrentWeekAndCalendar]);

  // Step 2: Set displayed week to current week
  useEffect(() => {
    if (isInitialized && currentWeek !== null && currentWeek !== undefined && displayedWeek === null) {
      console.log('2. Setting displayed week to current week:', currentWeek);
      setDisplayedWeek(currentWeek);
    }
  }, [isInitialized, currentWeek, displayedWeek]);

  // Step 3: Fetch games for the displayed week
  useEffect(() => {
    if (displayedWeek !== null && displayedWeek !== undefined && effectiveSeason !== null) {
      console.log('3. Fetching games, watched picks, and requesting locks for week:', displayedWeek);
      fetchGames(displayedWeek);
      fetchLiveGameData();
      fetchWatchedPicks(displayedWeek);
    }
  }, [displayedWeek, effectiveSeason, fetchGames, fetchLiveGameData, fetchWatchedPicks]);

  useEffect(() => {
    if (displayedWeek !== null && displayedWeek !== undefined) {
      const timer = setTimeout(() => {
        fetchLocksForWeek(displayedWeek);
      }, 1000); // 1 second delay

      return () => clearTimeout(timer);
    }
  }, [displayedWeek, fetchLocksForWeek]);

  useEffect(() => {
    if (displayedWeek !== null && displayedWeek !== undefined) {
      // Filter watched picks for the displayed week
      const filteredWatchedPicks = watchedPicks.filter(pick => pick.weekNumber === displayedWeek);
      if (JSON.stringify(filteredWatchedPicks) !== JSON.stringify(watchedPicks)) {
        setWatchedPicks(filteredWatchedPicks);
      }
    }
  }, [displayedWeek, watchedPicks, setWatchedPicks]);

  // Step 4: Setup socket listeners
  useEffect(() => {
    if (isInitialized) {
      const socket = getSocket();
      if (socket) {
        socket.on('pick_update', handlePickUpdate);
        return () => {
          socket.off('pick_update', handlePickUpdate);
        };
      }
    }
  }, [isInitialized, handlePickUpdate]);

  // Listen for week change events from mobile header
  useEffect(() => {
    const handleWeekChange = (event) => {
      const newWeek = event.detail.week;
      console.log('Week change event received:', newWeek);
      setDisplayedWeek(newWeek);
    };

    window.addEventListener('weekChange', handleWeekChange);
    return () => {
      window.removeEventListener('weekChange', handleWeekChange);
    };
  }, []);

  const isWeekGamesReady = loadedGamesWeek === displayedWeek;
  const isWeekGamesLoading = isGamesLoading || !isWeekGamesReady;

  const getWeekLabel = useCallback((week) => {
    if (week.season_type === 'postseason' && week.week >= 17) {
      const bowlWeekNumber = week.week - 16; // Week 17 = BW1, Week 18 = BW2
      return `BW${bowlWeekNumber}`;
    }
    return `Week ${week.week}`;
  }, []);

  const visibleWeeks = useMemo(() =>
    calendar ? calendar.filter(week => !week.hide_week) : []
  , [calendar]);

  const handleCurrentWeek = useCallback(() => {
    setDisplayedWeek(currentWeek);
    fetchWatchedPicks(currentWeek);
    fetchLocksForWeek(currentWeek);
    fetchUserSeasonLocks();
  }, [currentWeek, fetchWatchedPicks, fetchLocksForWeek, fetchUserSeasonLocks]);

  // Note: hasReachedMaxPicks logic is now handled in the enhanced game processing useMemo

  // Enhanced game processing with sorting, grouping, and league visibility
  const {
    leagueLockGames,
    ungroupedGames,
    sortResult,
    shouldShowLeagueLocksSection
  } = useMemo(() => {
    // Apply filters to games
    const filteredGames = applyFilters(games, filters);

    // Get user lock status for this week
    const locksForWeek = getLocksForWeek(displayedWeek);
    const maxPicksForWeek = getMaxPicksForWeek(displayedWeek);
    const userLocksCount = locksForWeek ? locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length : 0;
    const hasUserReachedMaxLocks = userLocksCount >= maxPicksForWeek;

    // Determine league visibility state
    const showLocks = canSeeLocks();
    const showLockers = canSeeLockers(displayedWeek, userLocksCount > 0, userLocksCount, maxPicksForWeek);
    const showLeagueLocksSection = sortSettings.showLeagueLocksGroup;

    // Apply sorting and grouping
    const sortResult = sortAndGroupGames(filteredGames, sortSettings);

    // Helper function to check if lock is active
    const isActiveLock = (lock) => {
      return lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress';
    };

    // Helper function to find game by ID
    const findGameById = (gameId) => {
      return filteredGames.find(g => g.id === gameId);
    };

    // Helper function to identify games with locks
    const identifyGamesWithLocks = () => {
      const gamesWithLocks = new Set();
      locksForWeek?.forEach(lock => {
        if (isActiveLock(lock)) {
          const game = findGameById(lock.gameId);
          if (game) {
            gamesWithLocks.add(game.id);
          }
        }
      });
      return gamesWithLocks;
    };

    const gamesWithLocks = identifyGamesWithLocks();

    // Helper function to create generic lock cards
    const createGenericCards = () => {
      const cards = [];
      if (!showLocks && !hasUserReachedMaxLocks && showLeagueLocksSection) {
        locksForWeek?.forEach(lock => {
          if (isActiveLock(lock)) {
            const game = findGameById(lock.gameId);
            if (game) {
              cards.push({
                id: `generic-${lock.id}`,
                game: game,
                lock: lock,
                lockTime: lock.createdAt,
                username: showLockers ? lock.username : 'Hidden',
                isGenericCard: true
              });
            }
          }
        });
      }
      return cards;
    };

    const genericCards = createGenericCards();

    // Process league lock games (detailed cards)
    const leagueLockGames = [];
    if (showLocks || hasUserReachedMaxLocks) {
      // States 1, 3, or post-lock: show detailed game cards in league locks
      // Create one entry per game, not per lock
      const gamesWithLocksSet = new Set();
      locksForWeek?.forEach(lock => {
        if (isActiveLock(lock)) {
          const game = findGameById(lock.gameId);
          if (game && !gamesWithLocksSet.has(game.id)) {
            gamesWithLocksSet.add(game.id);
            // Use the first lock for this game as the representative entry
            const firstLock = locksForWeek.find(l => l.gameId === game.id && isActiveLock(l));
            leagueLockGames.push({
              ...firstLock,
              game: game,
              lockTime: firstLock.createdAt,
              allLocks: locksForWeek.filter(l => l.gameId === game.id)
            });
          }
        }
      });
    }

    // Sort league lock games based on setting
    if (sortSettings.sortLeagueLocksByLockTime) {
      // Sort by lock time (default behavior)
      leagueLockGames.sort((a, b) => new Date(a.lockTime) - new Date(b.lockTime));
      genericCards.sort((a, b) => new Date(a.lockTime) - new Date(b.lockTime));
    } else {
      // Sort using the same logic as the main body
             const sortGameBySetting = (a, b) => {
         switch (sortSettings.sortBy) {
          case 'kickoff': {
            // Use the same kickoff time logic as sortUtils
            const aStatus = getGameStatus(a.game);
            const bStatus = getGameStatus(b.game);
            if (aStatus !== bStatus) {
              return aStatus - bStatus;
            }
            return new Date(a.game.start_date) - new Date(b.game.start_date);
          }
          case 'chronological':
            return new Date(a.game.start_date) - new Date(b.game.start_date);
          case 'teamRanking': {
            const aRank = Math.min(a.game.home_team?.rank || 999, a.game.away_team?.rank || 999);
            const bRank = Math.min(b.game.home_team?.rank || 999, b.game.away_team?.rank || 999);
            if (aRank === 999 && bRank === 999) return 0;
            if (aRank === 999) return 1;
            if (bRank === 999) return -1;
            return aRank - bRank;
          }
          case 'spreadSize': {
            const aSpread = Math.abs(a.game.betting?.spread || 0);
            const bSpread = Math.abs(b.game.betting?.spread || 0);
            return aSpread - bSpread;
          }
          case 'totalPoints': {
            const aTotal = a.game.betting?.over_under || 0;
            const bTotal = b.game.betting?.over_under || 0;
            return aTotal - bTotal;
          }
          case 'homeTeam':
            return (a.game.home_team?.name || '').localeCompare(b.game.home_team?.name || '');
          case 'awayTeam':
            return (a.game.away_team?.name || '').localeCompare(b.game.away_team?.name || '');
          default:
            return new Date(a.game.start_date) - new Date(b.game.start_date);
        }
      };

      leagueLockGames.sort((a, b) => {
        const comparison = sortGameBySetting(a, b);
        return sortSettings.sortDirection === 'desc' ? -comparison : comparison;
      });
      genericCards.sort((a, b) => {
        const comparison = sortGameBySetting(a, b);
        return sortSettings.sortDirection === 'desc' ? -comparison : comparison;
      });
    }

    // Process main body games
    let mainBodyGames = [];

    if (sortResult.ungroupedGames.length > 0) {
      // No grouping: use ungrouped games
      if (showLeagueLocksSection) {
        // League locks section is visible: filter out games with locks
        mainBodyGames = sortResult.ungroupedGames.filter(game => !gamesWithLocks.has(game.id));
      } else {
        // League locks section is hidden: include all games
        mainBodyGames = sortResult.ungroupedGames;
      }
    } else {
      // Grouped: flatten groups
      sortResult.groups.forEach(group => {
        group.games.forEach(game => {
          if (showLeagueLocksSection) {
            // League locks section is visible: filter out games with locks
            if (!gamesWithLocks.has(game.id)) {
              mainBodyGames.push(game);
            }
          } else {
            // League locks section is hidden: include all games
            mainBodyGames.push(game);
          }
        });
      });
    }

    // For States 2 & 4, keep games in main body even if they have locks
    // This applies whether the league locks section is visible or hidden
    if (!showLocks && !hasUserReachedMaxLocks) {
      const gamesToKeepInMain = filteredGames.filter(game => gamesWithLocks.has(game.id));
      // Remove duplicates by using a Set
      const existingGameIds = new Set(mainBodyGames.map(g => g.id));
      const uniqueGamesToAdd = gamesToKeepInMain.filter(game => !existingGameIds.has(game.id));
      mainBodyGames = [...mainBodyGames, ...uniqueGamesToAdd];
      console.log('State 2/4: Added games with locks to main body:', uniqueGamesToAdd.map(g => g.id));
    }

    // Debug logging
    console.log('League visibility state:', { showLocks, hasUserReachedMaxLocks, showLeagueLocksSection });
    console.log('Locks for week:', locksForWeek);
    console.log('Filtered games count:', filteredGames.length);
    console.log('Games with locks:', Array.from(gamesWithLocks));
    console.log('League lock games count:', leagueLockGames.length);
    console.log('Generic cards count:', genericCards.length);

    // Check if locked games are in main body
    const lockedGamesInBody = mainBodyGames.some(game => gamesWithLocks.has(game.id));
    console.log('Locked games in body:', lockedGamesInBody);
    if (lockedGamesInBody) {
      console.log('Locked games found in main body:', mainBodyGames.filter(game => gamesWithLocks.has(game.id)).map(g => g.id));
    }

    // Apply sorting to main body games
    const sortedMainBodyGames = sortGamesByDate(mainBodyGames);

    // Create groups for rendering based on the main body games (which include games with locks in States 2 & 4)
    let displayGroups = [];
    if (sortSettings.groupBy !== 'none') {
      // Re-group the main body games using sortAndGroupGames
      const regroupedResult = sortAndGroupGames(sortedMainBodyGames, sortSettings);
      displayGroups = regroupedResult.groups;
    }

    return {
      leagueLockGames: [...leagueLockGames, ...genericCards],
      ungroupedGames: sortedMainBodyGames,
      sortResult: { ...sortResult, groups: displayGroups }, // Use display groups that include games with locks
      shouldShowLeagueLocksSection: showLeagueLocksSection && (leagueLockGames.length > 0 || genericCards.length > 0)
    };
  }, [games, filters, sortSettings, displayedWeek, user, getLocksForWeek, getMaxPicksForWeek, canSeeLocks, canSeeLockers]);

  // Add helper function to get animation class
  const getAnimationClass = useCallback((gameId) => {
    const animationState = animatingGames[gameId];
    if (animationState) {
      return styles[`game${animationState.charAt(0).toUpperCase() + animationState.slice(1)}`];
    }
    return '';
  }, [animatingGames]);

  const hasReachedMaxPicksForWeek = useCallback(() => {
    if (!displayedWeek || !locksCache[displayedWeek]) return false;
    const locksForWeek = locksCache[displayedWeek];
    const userLocksCount = locksForWeek.filter((lock) =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;
    return userLocksCount >= getMaxPicksForWeek(displayedWeek);
  }, [displayedWeek, locksCache, user, getMaxPicksForWeek]);

  const openGameDetail = useCallback((game, initialSelectedPick = null) => {
    setGameDetailState({ game, initialSelectedPick });
  }, []);

  const closeGameDetail = useCallback(() => {
    setGameDetailState(null);
  }, []);

  const handleGameDetailPickAction = useCallback(async ({ gameId, pickType, lockLine, action }) => {
    const weekToUse = displayedWeek || currentWeek;
    if (!weekToUse) return;

    const game = games.find((g) => g.id === gameId);
    if (action === 'watch') {
      const storedLiveLine = pickType === 'over' || pickType === 'under'
        ? game?.betting?.over_under ?? lockLine
        : game?.betting?.spread ?? lockLine;
      toggleWatchPick(gameId, pickType, weekToUse, lockLine, storedLiveLine, isBetSlipOpen);
    } else if (action === 'lock') {
      const homeTeam = typeof game?.home_team === 'object' ? game.home_team.name : game?.home_team;
      const awayTeam = typeof game?.away_team === 'object' ? game.away_team.name : game?.away_team;
      requestDirectLock(
        {
          gameId,
          pickType,
          lockLine,
          liveLine: pickType === 'over' || pickType === 'under'
            ? game?.betting?.over_under
            : game?.betting?.spread,
          weekNumber: weekToUse,
          id: `${gameId}_${pickType}`,
          team: pickType === 'home'
            ? homeTeam
            : pickType === 'away'
              ? awayTeam
              : pickType.charAt(0).toUpperCase() + pickType.slice(1),
        },
        weekToUse
      );
    }
  }, [
    displayedWeek,
    currentWeek,
    games,
    toggleWatchPick,
    requestDirectLock,
    isBetSlipOpen,
  ]);

  // Enhanced renderGameCards function with sorting, grouping, and league visibility
  const renderGameCards = useCallback(() => {
    if (!games || games.length === 0) {
      const weekLabel = formatWeekLabel(displayedWeek, calendar);
      return (
        <div className={styles.emptyState}>
          <p>There are no scheduled games available for {weekLabel}. Check back later.</p>
        </div>
      );
    }

    const watchedPicksForWeek = watchedPicks.filter(pick => pick.weekNumber === displayedWeek);

    return (
      <>
        {/* League Locks Section */}
        {shouldShowLeagueLocksSection && (
          <React.Fragment>
            <div ref={locksRef} id="league-locks-section" className={styles.dateHeader}>
              League Locks
            </div>
            {leagueLockGames.map(lockEntry => {
              // Determine if this is a generic card or detailed card
              const isGenericCard = lockEntry.isGenericCard === true;

              if (isGenericCard) {
                return (
                  <LockedGameCard
                    key={lockEntry.id}
                    lock={lockEntry}
                    weekNumber={displayedWeek}
                    className={getAnimationClass(lockEntry.game.id)}
                  />
                );
              } else {
                // Show detailed game card
                const game = lockEntry.game;
                if (game) {
                  return (
                    <GameCard
                      key={`${game.id}-${lockEntry.id}`}
                      game={game}
                      userPick={findUserPick(userPicks, game.id)}
                      watchedPicks={watchedPicksForWeek}
                      displayedWeek={displayedWeek}
                      isBetSlipOpen={isBetSlipOpen}
                      onOpenGameDetail={openGameDetail}
                      className={getAnimationClass(game.id)}
                    />
                  );
                }
                return null;
              }
            })}
          </React.Fragment>
        )}

        {/* Main Body Games Section */}
        {sortSettings.groupBy === 'none' ? (
          // No grouping: show flat list with header
          <>
            <div className={styles.dateHeader}>
              All Week {displayedWeek} Games ({ungroupedGames.length})
            </div>
            {ungroupedGames.map(game => (
              <GameCard
                key={game.id}
                game={game}
                userPick={findUserPick(userPicks, game.id)}
                watchedPicks={watchedPicksForWeek}
                displayedWeek={displayedWeek}
                isBetSlipOpen={isBetSlipOpen}
                onOpenGameDetail={openGameDetail}
                className={getAnimationClass(game.id)}
              />
            ))}
          </>
        ) : (
          // Grouped: show by groups using sortResult
          sortResult.groups.map((group) => {
            const isCollapsed = isGroupCollapsed(group.key);
            return (
              <React.Fragment key={group.key}>
                <button
                  className={`${styles.dateHeader} ${styles.collapsibleHeader}`}
                  onClick={() => toggleGroupCollapse(group.key)}
                  title={isCollapsed ? "Click to expand" : "Click to collapse"}
                  aria-expanded={!isCollapsed}
                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${group.title} group`}
                >
                  <span className={styles.chevron}>
                    {isCollapsed ? <UnfoldMoreSharp /> : <UnfoldLessSharp />}
                  </span>
                  <span className={styles.groupTitle}>
                    {group.title} ({group.games.length})
                  </span>
                </button>
                {!isCollapsed && (
                  <>
                    {group.games.map(game => (
                      <GameCard
                        key={game.id}
                        game={game}
                        userPick={findUserPick(userPicks, game.id)}
                        watchedPicks={watchedPicksForWeek}
                        displayedWeek={displayedWeek}
                        isBetSlipOpen={isBetSlipOpen}
                        onOpenGameDetail={openGameDetail}
                        className={getAnimationClass(game.id)}
                      />
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })
        )}
      </>
    );
  }, [games, userPicks, watchedPicks, displayedWeek, calendar, isBetSlipOpen, openGameDetail, leagueLockGames, ungroupedGames, sortResult, shouldShowLeagueLocksSection, sortSettings.groupBy, getAnimationClass, isGroupCollapsed, toggleGroupCollapse]);

  const enrichWatchedPicks = useCallback((watchedPicks) => {
    return watchedPicks.map(pick => {
      const game = games.find(g => g.id === pick.gameId);
      if (game) {
        const homeTeam = typeof game.home_team === 'object' ? game.home_team.name : game.home_team;
        const awayTeam = typeof game.away_team === 'object' ? game.away_team.name : game.away_team;
        return {
          ...pick,
          team: pick.pickType === 'home' ? homeTeam : awayTeam,
          opponent: pick.pickType === 'home' ? awayTeam : homeTeam,
          gameDate: game.start_date,
          watchLine: pick.watchLine || 0,
          liveLine: pick.liveLine ?? (
            pick.pickType === 'home' || pick.pickType === 'away'
              ? game.betting?.spread
              : game.betting?.over_under
          ),
        };
      }
      return {
        ...pick,
        team: 'Unknown Team',
        opponent: 'Unknown Opponent',
        gameDate: 'Date TBD',
        watchLine: pick.watchLine || 0,
        liveLine: pick.liveLine || 0
      };
    });
  }, [games]);

  const enrichLockedPicks = useCallback((lockedPicks) => {
    return lockedPicks.map(lock => {
      return {
        ...lock,
        team: lock.pickType === 'home' ? lock.homeTeam : lock.awayTeam,
        opponent: lock.pickType === 'home' ? lock.awayTeam : lock.homeTeam,
      };
    });
  }, []);

  const toggleBetSlip = useCallback(() => {
    setIsBetSlipOpen(prevState => {
      return !prevState;
    });
  }, []);

  const renderBetSlip = useCallback(() => {
    if (displayedWeek !== null && calendar && calendar.length > 0) {
      const watchedPicksForWeek = watchedPicks.filter(pick => pick.weekNumber === displayedWeek);
      const enrichedWatchedPicks = enrichWatchedPicks(watchedPicksForWeek);
      const locksForWeek = locksCache[displayedWeek] || [];
      const enrichedLocks = enrichLockedPicks(locksForWeek);
      return (
        <BetSlip
          week={displayedWeek}
          calendar={calendar}
          onClose={() => setIsBetSlipOpen(false)}
          watchedPicks={enrichedWatchedPicks}
          setWatchedPicks={setWatchedPicks}
          toggleOpen={toggleBetSlip}
          locks={enrichedLocks}
          userId={user.id}
          isBetSlipOpen={isBetSlipOpen}
          byeWeekState={byeWeekState}
          games={games}
        />
      );
    }
    return null;
  }, [
    displayedWeek,
    calendar,
    watchedPicks,
    enrichWatchedPicks,
    setWatchedPicks,
    toggleBetSlip,
    locksCache,
    enrichLockedPicks,
    user,
    isBetSlipOpen,
    byeWeekState,
    games
  ]);

  // NOW we can have conditional returns after all hooks are called
  if (isMobile) {
    return (
      <MobileWeeklyPickSheet
        displayedWeek={displayedWeek}
        onWeekChange={(newWeek) => {
          setDisplayedWeek(newWeek);
          fetchWatchedPicks(newWeek);
          fetchLocksForWeek(newWeek);
          fetchUserSeasonLocks();
        }}
      />
    );
  }

  // Extract the find function to a separate helper
  const findUserPick = (userPicks, gameId) => userPicks.find(pick => pick.game_id === gameId);

  return (
    <div className={styles.weeklyPickSheet}>
      <IntegratedHeader
        displayedWeek={displayedWeek}
        visibleWeeks={visibleWeeks}
        currentWeek={currentWeek}
        setDisplayedWeek={(week) => {
          setDisplayedWeek(week);
          fetchWatchedPicks(week);
          fetchLocksForWeek(week);
          fetchUserSeasonLocks();
        }}
        fetchWatchedPicks={fetchWatchedPicks}
        handleCurrentWeek={handleCurrentWeek}
        getWeekLabel={getWeekLabel}
        games={games}
      />

      <div className={styles.contentPadding}>
        {(isLoading ||
          isWeekAndCalendarLoading ||
          displayedWeek === null ||
          isWeekGamesLoading) ? (
          <GameCardSkeletonGrid count={6} />
        ) : (
          <div className={styles.gameGrid}>
            {renderGameCards()}
          </div>
        )}
      </div>

      <button className={styles.pickSlipButton} onClick={toggleBetSlip}>
        {isBetSlipOpen ? 'Close PickSlip' : 'Open PickSlip'}
      </button>

      <div className={`${styles.betSlipContainer} ${isBetSlipOpen ? styles.open : ''} ${isPeeking ? styles.peeking : ''}`}>
        {renderBetSlip()}
      </div>

      {gameDetailState && (
        <GameDetailModal
          game={gameDetailState.game}
          displayedWeek={displayedWeek}
          isOpen={Boolean(gameDetailState)}
          onClose={closeGameDetail}
          onPickAction={handleGameDetailPickAction}
          hasReachedMaxPicks={hasReachedMaxPicksForWeek()}
          initialSelectedPick={gameDetailState.initialSelectedPick}
        />
      )}
    </div>
  );
};

export default React.memo(WeeklyPickSheet);
